import { Formik, Form, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

// Shared
import SubmitButton from '@atoms/SubmitButton';
import TextInput from '@atoms/TextInput';

// Functionality
import { LoadingContext } from '@context/LoadingProvider';
import { searchMemberByName } from '@general/api/memberAPI';

const getFullName = (firstName, lastName) => {
    return `${firstName} ${lastName}`;
};

const SubmitWhenValid = ({ lastSubmittedName }) => {
    const { isSubmitting, isValid, submitForm, values } = useFormikContext();

    useEffect(() => {
        // Yup validation
        if (!isValid) {
            return null;
        }

        // Require first and last name
        if (!values.firstName || !values.lastName) {
            return null;
        }

        const fullName = getFullName(values.firstName, values.lastName);

        // Early exit if it's the same name we've already submitted
        if (fullName === lastSubmittedName) {
            return null;
        }

        // Submit since name hasn't previously been searched
        if (isValid && fullName !== lastSubmittedName && !isSubmitting) {
            submitForm();
        }
    }, [isSubmitting, isValid, lastSubmittedName, submitForm, values]);

    return null;
};

const SearchMembersByName = ({ onSearchCompleted }) => {
    const { setIsLoading } = useContext(LoadingContext);
    const [lastSubmittedName, setLastSubmittedName] = useState('');

    const handleSubmit = async data => {
        const userFullName = getFullName(data.firstName, data.lastName);
        setLastSubmittedName(userFullName);
        setIsLoading(true);

        try {
            const response = await searchMemberByName({
                firstName: data.firstName,
                lastName: data.lastName,
            });

            onSearchCompleted(response.data?.data);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <Formik
                initialValues={{
                    firstName: '',
                    lastName: '',
                }}
                onSubmit={handleSubmit}
                validationSchema={Yup.object().shape({
                    firstName: Yup.string()
                        .required('Required')
                        .min(2, 'Miminum 2 characters.'),
                    lastName: Yup.string()
                        .required('Required')
                        .min(2, 'Miminum 2 characters.'),
                })}
                validateOnBlur
                validateOnChange={false}
            >
                {({ isSubmitting }) => (
                    <Form>
                        <TextInput name="firstName" placeholder="First Name" required type="text" />
                        <TextInput name="lastName" placeholder="Last Name" required type="text" />

                        <SubmitButton isSubmitting={isSubmitting} submittingText="Searching by name">
                            Search by name
                        </SubmitButton>

                        <SubmitWhenValid lastSubmittedName={lastSubmittedName} />
                    </Form>
                )}
            </Formik>
        </div>
    );
};

SearchMembersByName.propTypes = {
    onSearchCompleted: PropTypes.func.isRequired,
};

export default SearchMembersByName;
